import { z } from 'zod'
import { mcpAccessLevelSchema } from '../enums/mcp-access-level.enum.js'
import { mcpConfirmationStatusSchema } from '../enums/mcp-confirmation-status.enum.js'
import { mcpOrgRemovalReasonSchema } from '../enums/mcp-org-removal-reason.enum.js'
import { mcpOrgSettingSchema } from '../enums/mcp-org-setting.enum.js'
import { recognizedAppMatchSchema } from '../enums/recognized-app-match.enum.js'
import { staffRoleSchema } from '../enums/staff-role.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { tenantRoleSchema } from '../enums/tenant-role.enum.js'
import { tenantStatusSchema } from '../enums/tenant-status.enum.js'
import { uuidSchema } from '../validators/params.js'

/** An AI app as PixelSign shows it: the name it gives itself, the web address it signs in from, and whether PixelSign recognizes it. */
export const mcpAppDtoSchema = z.object({
  name: z.string(),
  origin: z.string(),
  recognized: z.boolean(),
})

export type McpAppDto = z.infer<typeof mcpAppDtoSchema>

/** What the connect page shows before anyone signs in. */
export const consentRequestAppDtoSchema = z.object({
  app: mcpAppDtoSchema,
  expiresAt: z.string().datetime(),
})

export type ConsentRequestAppDto = z.infer<typeof consentRequestAppDtoSchema>

/** The signed-in person on the connect page: the name and email they should recognize as their own. */
export const consentUserSchema = z.object({
  name: z.string(),
  email: z.string(),
})

export type ConsentUserDto = z.infer<typeof consentUserSchema>

/** One organization on the consent screen: what the person can grant there. */
export const consentOrgOptionSchema = z.object({
  tenantId: uuidSchema,
  name: z.string(),
  slug: z.string(),
  role: tenantRoleSchema,
  status: tenantStatusSchema,
  setting: mcpOrgSettingSchema,
  /** The levels the person may choose; empty when the organization has MCP turned off. */
  offeredLevels: z.array(mcpAccessLevelSchema),
})

export type ConsentOrgOption = z.infer<typeof consentOrgOptionSchema>

export const consentOptionsDtoSchema = z.discriminatedUnion('as', [
  z.object({
    as: z.literal('tenant_user'),
    app: mcpAppDtoSchema,
    user: consentUserSchema,
    organizations: z.array(consentOrgOptionSchema),
    expiresAt: z.string().datetime(),
  }),
  z.object({
    as: z.literal('staff'),
    app: mcpAppDtoSchema,
    user: consentUserSchema,
    role: staffRoleSchema,
    /** Whether "Make changes" may be chosen: admins, from a recognized app. */
    offerChanges: z.boolean(),
    expiresAt: z.string().datetime(),
  }),
])

export type ConsentOptionsDto = z.infer<typeof consentOptionsDtoSchema>

/** Where the browser goes next: back to the AI app, with a code or an error. */
export const consentResultDtoSchema = z.object({ redirectUrl: z.string() })

export type ConsentResultDto = z.infer<typeof consentResultDtoSchema>

/** How an AI app can show PixelSign's confirmation: a PixelSign page it opens, a prompt in the app, or not at all. */
export const mcpConfirmationSupportSchema = z.enum(['url', 'form', 'none'])

export type McpConfirmationSupport = z.infer<typeof mcpConfirmationSupportSchema>

/** What's left of the organization's MCP send-and-remind allowance in one mode; `resetsAt` is null while it's untouched. */
export const allowanceDtoSchema = z.object({
  remaining: z.number().int(),
  limit: z.number().int(),
  resetsAt: z.string().datetime().nullable(),
})

export type AllowanceDto = z.infer<typeof allowanceDtoSchema>

/** get_connection_info for an API key: the one organization and mode it acts in. */
export const keyConnectionInfoDtoSchema = z.object({
  as: z.literal('api_key'),
  organization: z.object({ id: uuidSchema, name: z.string(), slug: z.string() }),
  mode: tenantModeSchema,
  key: z.object({ name: z.string(), prefix: z.string() }),
  /** The name the AI app gave itself, if it did. */
  app: z.string().nullable(),
  allowance: allowanceDtoSchema,
  confirmations: mcpConfirmationSupportSchema,
  notes: z.string(),
})

export type KeyConnectionInfoDto = z.infer<typeof keyConnectionInfoDtoSchema>

/** One organization on a member's connection: what was granted, and what applies now. */
export const connectionOrgDtoSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  name: z.string(),
  /** Null when the person is no longer a member. */
  role: tenantRoleSchema.nullable(),
  status: tenantStatusSchema,
  setting: mcpOrgSettingSchema,
  granted: mcpAccessLevelSchema,
  /** The level in effect now (setting, role, and app recognition applied); null when unavailable. */
  level: mcpAccessLevelSchema.nullable(),
  /** Removed from the connection, no longer a member, or the organization closed. */
  removed: z.boolean(),
})

export type ConnectionOrgDto = z.infer<typeof connectionOrgDtoSchema>

/** get_connection_info for an organization member's connection. */
export const memberConnectionInfoDtoSchema = z.object({
  as: z.literal('tenant_user'),
  person: z.object({ name: z.string(), email: z.string() }),
  app: mcpAppDtoSchema,
  organizations: z.array(connectionOrgDtoSchema),
  confirmations: mcpConfirmationSupportSchema,
  connectionExpiresAt: z.string().datetime(),
  notes: z.string(),
})

export type MemberConnectionInfoDto = z.infer<typeof memberConnectionInfoDtoSchema>

/** get_connection_info for a PixelSign staff connection. */
export const staffConnectionInfoDtoSchema = z.object({
  as: z.literal('staff'),
  person: z.object({ name: z.string(), email: z.string() }),
  role: staffRoleSchema,
  canChange: z.boolean(),
  app: mcpAppDtoSchema,
  confirmations: mcpConfirmationSupportSchema,
  connectionExpiresAt: z.string().datetime(),
  notes: z.string(),
})

export type StaffConnectionInfoDto = z.infer<typeof staffConnectionInfoDtoSchema>

/** What PixelSign asks the person to confirm: the action, where, and the details that matter. */
export const confirmationSummarySchema = z.object({
  action: z.string(),
  confirmLabel: z.string(),
  /** The confirm button is red (voids, deletes). */
  destructive: z.boolean(),
  organizationName: z.string().nullable(),
  mode: tenantModeSchema.nullable(),
  items: z.array(z.object({ label: z.string(), value: z.string() })),
})

export type ConfirmationSummary = z.infer<typeof confirmationSummarySchema>

/** A confirmation as PixelSign's confirm page shows it. */
export const confirmationDtoSchema = z.object({
  id: uuidSchema,
  app: mcpAppDtoSchema,
  summary: confirmationSummarySchema,
  status: mcpConfirmationStatusSchema,
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
})

export type ConfirmationDto = z.infer<typeof confirmationDtoSchema>

/** A short-lived link to one envelope file, for an AI app to fetch (it expires in minutes and is re-checked on every fetch). */
export const downloadLinkDtoSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
  filename: z.string(),
})

export type DownloadLinkDto = z.infer<typeof downloadLinkDtoSchema>

/** Where an AI app uploads a local PDF for an envelope it's about to create. */
export const uploadLinkDtoSchema = z.object({
  uploadId: uuidSchema,
  url: z.string().url(),
  method: z.literal('PUT'),
  contentType: z.literal('application/pdf'),
  maxBytes: z.number().int(),
  expiresAt: z.string().datetime(),
  instructions: z.string(),
})

export type UploadLinkDto = z.infer<typeof uploadLinkDtoSchema>

/** One organization on a person's connection: the level granted there now, and whether it was taken off. */
export const connectionOrgAccessDtoSchema = z.object({
  tenantId: uuidSchema,
  name: z.string(),
  level: mcpAccessLevelSchema,
  removed: z.boolean(),
  removedReason: mcpOrgRemovalReasonSchema.nullable(),
  lastUsedAt: z.string().datetime().nullable(),
})

/** A connection on the person's own Connected apps page. */
export const myConnectionDtoSchema = z.object({
  id: uuidSchema,
  app: mcpAppDtoSchema,
  /** The browser and system the person connected from, as far as it can be told. */
  device: z.string().nullable(),
  signInIp: z.string().nullable(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime(),
  /** When it lapses if it stays unused. */
  expiresAt: z.string().datetime(),
  /** Live changes (or staff changes) are allowed, so the shorter lifetime applies. */
  elevated: z.boolean(),
  organizations: z.array(connectionOrgAccessDtoSchema),
})

export type MyConnectionDto = z.infer<typeof myConnectionDtoSchema>

export const myConnectionListDtoSchema = z.object({ connections: z.array(myConnectionDtoSchema) })

export type MyConnectionListDto = z.infer<typeof myConnectionListDtoSchema>

/** A member's connection as an Owner sees it: only this organization's part. */
export const orgConnectionDtoSchema = z.object({
  id: uuidSchema,
  person: z.object({ userId: uuidSchema, name: z.string(), email: z.string() }),
  app: mcpAppDtoSchema,
  level: mcpAccessLevelSchema,
  lastUsedAt: z.string().datetime().nullable(),
  connectedAt: z.string().datetime(),
})

export type OrgConnectionDto = z.infer<typeof orgConnectionDtoSchema>

export const orgConnectionListDtoSchema = z.object({ connections: z.array(orgConnectionDtoSchema) })

export type OrgConnectionListDto = z.infer<typeof orgConnectionListDtoSchema>

/** A staff member's connection, on the ops console. */
export const staffConnectionDtoSchema = z.object({
  id: uuidSchema,
  staff: z.object({ id: uuidSchema, name: z.string(), email: z.string() }),
  app: mcpAppDtoSchema,
  canChange: z.boolean(),
  device: z.string().nullable(),
  signInIp: z.string().nullable(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
})

export type StaffConnectionDto = z.infer<typeof staffConnectionDtoSchema>

export const staffConnectionListDtoSchema = z.object({ connections: z.array(staffConnectionDtoSchema) })

export type StaffConnectionListDto = z.infer<typeof staffConnectionListDtoSchema>

/** An organization's cap on AI apps over MCP. */
export const mcpSettingDtoSchema = z.object({ setting: mcpOrgSettingSchema })

export type McpSettingDto = z.infer<typeof mcpSettingDtoSchema>

/** An AI app staff trust with live and staff-change grants. */
export const recognizedAppDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  match: recognizedAppMatchSchema,
  value: z.string(),
  /** The staff member who added it. */
  addedBy: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type RecognizedAppDto = z.infer<typeof recognizedAppDtoSchema>

export const recognizedAppListDtoSchema = z.object({ apps: z.array(recognizedAppDtoSchema) })

export type RecognizedAppListDto = z.infer<typeof recognizedAppListDtoSchema>

/** One way an app identifies itself when signing in, and whether it can go on the recognized list. */
export const seenAppIdentityDtoSchema = z.object({
  match: recognizedAppMatchSchema,
  value: z.string(),
  /** Why it can't be recognized (an address on the person's computer, plain http), or null when it can. */
  problem: z.string().nullable(),
})

export type SeenAppIdentityDto = z.infer<typeof seenAppIdentityDtoSchema>

/**
 * An AI app people have signed in with recently, as staff see it when
 * deciding what to recognize: exactly how it identifies itself, and whether
 * it's already on the list.
 */
export const seenAppDtoSchema = z.object({
  /** The app's client id: a registration's uuid, or its client ID metadata URL. */
  clientId: z.string(),
  /** The name the app gave itself (anyone can claim any name; trust the identities, not this). */
  name: z.string(),
  /** Its client ID metadata URL first (when it has one), then the redirect URIs it registered. */
  identities: z.array(seenAppIdentityDtoSchema),
  activeConnections: z.number().int().nonnegative(),
  lastUsedAt: z.string().datetime(),
  /** The recognized-app entry it matches, if any. */
  recognizedAs: z.string().nullable(),
})

export type SeenAppDto = z.infer<typeof seenAppDtoSchema>

export const seenAppListDtoSchema = z.object({ apps: z.array(seenAppDtoSchema) })

export type SeenAppListDto = z.infer<typeof seenAppListDtoSchema>

/** Whether PixelSign's MCP server is switched on (the web app shows its pages only then). */
export const mcpStatusDtoSchema = z.object({ enabled: z.boolean() })

export type McpStatusDto = z.infer<typeof mcpStatusDtoSchema>
